<template>
  <div class="feedback-box content">
    <stickers :essay="essay" />

    <base-item
      :item="overAllCriteria"
      :format-title="formatTitle"
      :index="-2"
      @update="updateEssay"
    />

    <report-items
      :items="itemsWithColors"
      :selected-item="selectedItem"
      @select-item="selectItem"
    />

    <base-item
      v-for="(item, index) in filteredItems"
      :ref="(el) => setItemRef(el, item.originalIndex)"
      :key="`${item.id}-${item.originalIndex}`"
      class="bordered"
      :class="index === 0 ? 'bordered-first-item' : ''"
      :active-highlight-index="activeHighlightIndex"
      :index="index"
      :item="item"
      :format-title="formatTitle"
      @updateActiveHighlightIndex="$emit('updateActiveHighlightIndex', index)"
      @update="updateEssayTabData"
      @delete="deleteEssayTabData"
    />
  </div>
</template>

<script>
import BaseItem from "@/components/essay/essay/v3/base_item.vue";
import generateBaseReportV3Mixin from "@/components/essay/essay/v3/mixins/base_report_v3";
import Stickers from "@/components/essay/essay/v3/stickers.vue";
import { mapGetters } from "vuex";
import stickerMixin from "@/components/essay/essay/v3/mixins/sticker_mixin";
import ReportItems from "@/components/essay/essay/v3/report_items.vue";
import refsMapMixin from "@/components/essay/essay/v3/mixins/refs_map_mixin";

const colors = [
  "#3A91AB",
  // "#A060FB",
  "#00D4FF",
  "#1A47B8",
  "#007AFF",
  // "#FF2D55",
  // "#FF9500",
  // "#FF3B30",
  "#00C853",
  // "#F1B500",
  "#CE00DD",
];

export default {
  components: {
    BaseItem,
    Stickers,
    ReportItems,
  },

  // this.activeHighlightIndex
  // this.items
  // this.essay
  // this.isNoRanges
  // this.updateEssayTabData
  // this.deleteEssayTabData
  // this.updateEssay
  // this.selectedItem
  // this.selectItem
  mixins: [generateBaseReportV3Mixin("feedback"), stickerMixin, refsMapMixin],

  computed: {
    ...mapGetters("config", ["features"]),
    itemsWithColors() {
      return this.items.map((item, index) => ({
        ...item,
        color: colors[(index + 1) % colors.length],
        backgroundColor: `${colors[(index + 1) % colors.length]}1A`,
        originalIndex: index,
      }));
    },

    filteredItems() {
      if (this.selectedItem === "all") {
        return this.itemsWithColors;
      }

      return this.itemsWithColors.filter(
        (item) => item.id === this.selectedItem
      );
    },

    shouldHidePercentages() {
      return (
        this.features?.feedbackCustomizations?.includes(
          "hidePercentagesInGrades"
        ) || false
      );
    },
    gradeInfo() {
      return this.items?.reduce(
        (acum, feedback) => {
          return {
            grade: acum.grade + Number(feedback.grade),
            maxGrade: acum.maxGrade + feedback.criteria.maxGrade,
          };
        },
        {
          grade: 0,
          maxGrade: 0,
        }
      );
    },
    overAllCriteria() {
      if (!this.gradeInfo) return {};

      return {
        id: 2,
        criteria: {
          name: this.$t("essays.overallGrade"),
          maxGrade: parseFloat(this.gradeInfo?.maxGrade.toFixed(2)),
        },
        grade: parseFloat(this.gradeInfo.grade.toFixed(2)),
        percentage: parseFloat(
          (
            (this.gradeInfo.grade / this.gradeInfo?.maxGrade) * 100 || 0
          ).toFixed(2)
        ),
        analysis: this.essay?.summary?.feedback?.analysis,
        suggestions: this.essay?.summary?.feedback?.suggestions,
      };
    },
  },

  watch: {
    filteredItems() {
      if (this.isNoRanges) return;

      this.$nextTick(() => {
        // Create a map of all items with their original indices
        const itemIndexMap = new Map();
        this.itemsWithColors.forEach((item, idx) => {
          itemIndexMap.set(item, idx);
        });

        const highlights = this.filteredItems?.reduce((acc, feedback) => {
          if (!feedback.ranges) {
            return acc;
          }

          // Find the original index from the current itemsWithColors
          const originalIndex = itemIndexMap.get(feedback) ?? 0;

          for (
            let rangeIndex = 0;
            rangeIndex < feedback.ranges.length;
            rangeIndex++
          ) {
            acc.push({
              range: feedback.ranges[rangeIndex],
              highlightIndex: originalIndex + 1,
              showHighlightIndex: true,
              refComponent: this.itemRefs.get(originalIndex),
              color: feedback.color,
              backgroundColor: feedback.backgroundColor,
            });
          }

          return acc;
        }, []);

        this.$emit("updateHighlights", highlights);
      });
    },
  },

  methods: {
    formatTitle(item) {
      if (!item.criteria) return "";

      return `${item.criteria.name}: ${parseFloat(item.grade)}/${
        item.criteria.maxGrade
      } ${this.formatPercentage(item.percentage)}`;
    },
    formatPercentage(percentage) {
      if (percentage === undefined || this.shouldHidePercentages) {
        return "";
      }

      return `(${percentage}%)`;
    },
  },
};
</script>

<style lang="scss" scoped>
.feedback-box.ql-container {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
  padding-top: 0px;
}

.border-bottom {
  border-bottom: 1px solid #dfdfdf;
}
</style>
